import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Container, Modal, Spinner, Table } from "react-bootstrap";
import { Navigate, useSearchParams } from "react-router-dom";
import { FiArrowDown, FiArrowUp, FiCheckCircle, FiEdit2, FiEye, FiPlus, FiRefreshCw, FiSlash, FiTrash2 } from "react-icons/fi";
import PageBreadcrumb from "@core/components/PageBreadcrumb";
import StatusPill from "@core/components/StatusPill";
import TableActions from "@core/components/TableActions";
import TableActionButton from "@core/components/TableActionButton";
import { SearchBar, type FilterField } from "@core/components/SearchBar";
import { useDebounce } from "@core/hooks/useDebounce";
import { useScopeId } from "@core/hooks/useRouteParamId";
import { useEntityBasePath } from "@core/hooks/useEntityBasePath";
import { departmentsToPresentationLayoutsTrail } from "@core/navigation/pageBreadcrumbTrails";
import type { ErrorResponse } from "@core/types/errorResponse";
import type ApiResponse from "@core/types/apiResponse";
import type { ApiMessageResponse } from "@core/types/apiMessageResponse";
import { useApiError } from "@infrastructure/api/hooks/useApiError";
import { useErrorModalStore } from "@infrastructure/api/store/errorModalStore";
import { showSuccess } from "@infrastructure/api/utils/toastNotifications";
import { formatLayoutApiErrorMessages } from "@features/presentationLayout/utils/formatLayoutApiErrors";
import { usePermissions } from "@features/entity/hooks/usePermissions";
import {
  PRESENTATION_LAYOUT_MANAGE_CODES,
  getPresentationLayoutCodesForOrigin,
} from "@features/entity/types/actionCodes";
import { useDepartments } from "@features/department/hooks/useDepartments";
import { useDeductibleCatalog } from "@features/admin/finance/hooks/useDeductibleCatalog";
import StatusChangeConfirmModal, { type StatusChangeKind } from "@features/admin/finance/components/StatusChangeConfirmModal";
import type { OriginScope } from "@features/report/template/types/reportTemplate";
import {
  changePresentationLayoutOrigin,
  createPresentationLayout,
  deletePresentationLayout,
  disablePresentationLayout,
  enablePresentationLayout,
  fetchPresentationLayout,
  fetchPresentationLayoutMeta,
  queryPresentationLayouts,
  reorderPresentationLayouts,
  updatePresentationLayout,
} from "@features/presentationLayout/api/presentationLayoutApi";
import PresentationLayoutEditorModal from "@features/presentationLayout/components/PresentationLayoutEditorModal";
import PresentationLayoutPreviewModal from "@features/presentationLayout/components/PresentationLayoutPreviewModal";
import ChangePresentationLayoutOriginModal from "@features/presentationLayout/components/ChangePresentationLayoutOriginModal";
import {
  ORIGIN_SCOPES,
  ORIGIN_SCOPE_LABELS,
  type PresentationLayoutDetail,
  type PresentationLayoutListItem,
  type PresentationLayoutMeta,
  type PresentationLayoutQueryParams,
  type UpsertPresentationLayoutPayload,
} from "@features/presentationLayout/types/presentationLayout";
import { moveItem } from "@features/presentationLayout/utils/layoutForm";
import { MESSAGE_META_LOAD_FAILED } from "@features/presentationLayout/constants/presentationLayoutConstants";

const FILTER_FIELDS: FilterField[] = [
  {
    name: "originScope",
    label: "Report level",
    type: "select",
    options: ORIGIN_SCOPES.map((origin) => ({ value: origin, label: ORIGIN_SCOPE_LABELS[origin] })),
    colWidth: 6,
  },
  {
    name: "isActive",
    label: "Status",
    type: "select",
    options: [
      { value: "1", label: "Active" },
      { value: "0", label: "Inactive" },
    ],
    colWidth: 6,
  },
];

type EditorState = { layout: PresentationLayoutDetail | null } | null;
type StatusTarget = { kind: StatusChangeKind; layout: PresentationLayoutListItem } | null;

const STATUS_CHANGE_ENTITY_LABEL = "presentation layout";

const statusChangeBody = (kind: StatusChangeKind, name: string): React.ReactNode =>
  kind === "disable" ? (
    <>
      Are you sure you want to disable <strong>{name}</strong>? It will no longer show in the Layout list. Reports
      that were already submitted are unaffected.
    </>
  ) : (
    <>
      Are you sure you want to enable <strong>{name}</strong>? It will show in the Layout list. Reports that were
      already submitted are unaffected.
    </>
  );

const ManagePresentationLayouts: React.FC = () => {
  const [searchParams] = useSearchParams();
  const departmentId = Number(searchParams.get("did")) || 0;
  const nationId = useScopeId() ?? 0;
  const basePath = useEntityBasePath();
  const breadcrumbItems = useMemo(() => (basePath ? departmentsToPresentationLayoutsTrail(basePath) : []), [basePath]);
  const showError = useApiError();
  const showValidation = useErrorModalStore((s) => s.showValidation);

  const {
    hasPermission,
    hasAnyPermission,
    loading: permsLoading,
    error: permsError,
    refresh: refreshPermissions,
  } = usePermissions({
    scopeType: "NATION",
    scopeId: nationId || undefined,
    actionCodes: [...PRESENTATION_LAYOUT_MANAGE_CODES],
  });
  const canManageAny = hasAnyPermission([...PRESENTATION_LAYOUT_MANAGE_CODES]);
  const codesFor = (origin: string) => getPresentationLayoutCodesForOrigin(origin);
  const can = (origin: string, action: keyof NonNullable<ReturnType<typeof codesFor>>) => {
    const codes = codesFor(origin);
    return codes ? Boolean(hasPermission(codes[action])) : false;
  };

  const availableOriginScopes = useMemo(
    () => ORIGIN_SCOPES.filter((origin) => {
      const codes = getPresentationLayoutCodesForOrigin(origin);
      return codes ? Boolean(hasPermission(codes.create)) : false;
    }),
    [hasPermission]
  );

  const { departments } = useDepartments({ departmentId, ...(nationId ? { nationId } : {}) });
  const departmentName = departments?.length === 1 ? departments[0].name : undefined;
  const { catalog: deductibles } = useDeductibleCatalog(canManageAny);

  const [layouts, setLayouts] = useState<PresentationLayoutListItem[]>([]);
  const [locked, setLocked] = useState(false);
  const [lockedReason, setLockedReason] = useState<string | null>(null);
  const [meta, setMeta] = useState<PresentationLayoutMeta | null>(null);
  const [metaError, setMetaError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});
  const [editor, setEditor] = useState<EditorState>(null);
  const [previewTarget, setPreviewTarget] = useState<PresentationLayoutListItem | null>(null);
  const [originTarget, setOriginTarget] = useState<PresentationLayoutListItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PresentationLayoutListItem | null>(null);
  const [statusTarget, setStatusTarget] = useState<StatusTarget>(null);

  const debouncedQuery = useDebounce(searchQuery, 300);
  const appliedFilters = Object.fromEntries(Object.entries(activeFilters).filter(([, v]) => v !== "" && v != null));
  const activeFilterCount = Object.keys(appliedFilters).length;
  const isFiltered = Boolean(debouncedQuery) || activeFilterCount > 0;

  const load = useCallback(async () => {
    if (!nationId || !departmentId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const params: PresentationLayoutQueryParams = { all: true, sortBy: "list_order", sortDirection: "asc" };
      if (debouncedQuery) params.freetext = debouncedQuery;
      if (appliedFilters.originScope) params.originScope = appliedFilters.originScope;
      if (appliedFilters.isActive === "1") params.isActive = true;
      if (appliedFilters.isActive === "0") params.isActive = false;

      const result = await queryPresentationLayouts(nationId, departmentId, params);
      if (!result.isSuccess()) {
        setLoadError((result.getError() as ErrorResponse)?.detail || "Could not load presentation layouts.");
        setLayouts([]);
        return;
      }
      const payload = result.getData();
      setLayouts(payload?.data ?? []);
      setLocked(Boolean(payload?.locked));
      setLockedReason(payload?.locked_reason ?? null);
    } finally {
      setLoading(false);
    }
  }, [nationId, departmentId, debouncedQuery, appliedFilters.originScope, appliedFilters.isActive]);

  useEffect(() => {
    if (canManageAny) void load();
  }, [canManageAny, load]);

  useEffect(() => {
    if (!canManageAny || !nationId || !departmentId) return;
    setMetaError(false);
    void fetchPresentationLayoutMeta(nationId, departmentId)
      .then((result) => {
        if (result.isSuccess()) {
          setMeta(result.getData() ?? null);
          return;
        }
        setMeta(null);
        setMetaError(true);
      })
      .catch(() => {
        setMeta(null);
        setMetaError(true);
      });
  }, [canManageAny, nationId, departmentId]);

  /** Runs a write, shows the message, and reloads the list. Returns whether it succeeded. */
  const runWrite = async (request: () => Promise<ApiResponse<ApiMessageResponse>>, fallback: string): Promise<boolean> => {
    setBusy(true);
    try {
      const result = await request();
      if (!result.isSuccess()) {
        showError(result.getError() as ErrorResponse);
        return false;
      }
      showSuccess(result.getData()?.message ?? fallback);
      await load();
      return true;
    } catch (error) {
      showError(error as ErrorResponse);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const openEditor = async (layout: PresentationLayoutListItem | null) => {
    if (!layout) {
      setEditor({ layout: null });
      return;
    }
    setBusy(true);
    try {
      const result = await fetchPresentationLayout(nationId, departmentId, layout.presentation_layout_id);
      if (!result.isSuccess()) {
        showError(result.getError() as ErrorResponse);
        return;
      }
      setEditor({ layout: result.getData() ?? null });
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async (payload: UpsertPresentationLayoutPayload) => {
    const existing = editor?.layout ?? null;
    setBusy(true);
    try {
      const result = await (existing
        ? updatePresentationLayout(nationId, departmentId, existing.presentation_layout_id, payload)
        : createPresentationLayout(nationId, departmentId, payload));
      if (!result.isSuccess()) {
        const error = result.getError() as ErrorResponse;
        if (error?.errors && Object.keys(error.errors).length > 0) {
          showValidation(formatLayoutApiErrorMessages(error, payload.sections.map((section) => section.title)));
        } else {
          showError(error);
        }
        return;
      }
      showSuccess(result.getData()?.message ?? "Saved.");
      await load();
      setEditor(null);
    } catch (error) {
      const err = error as ErrorResponse;
      if (err?.errors && Object.keys(err.errors).length > 0) {
        showValidation(formatLayoutApiErrorMessages(err, payload.sections.map((section) => section.title)));
      } else {
        showError(err);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleMove = (index: number, to: number) => {
    const ids = moveItem(layouts, index, to).map((layout) => layout.presentation_layout_id);
    void runWrite(() => reorderPresentationLayouts(nationId, departmentId, ids), "Order updated.");
  };

  if (nationId > 0 && permsLoading) {
    return (
      <Container fluid className="py-5 d-flex justify-content-center" data-testid="presentation-layouts-perms-loading">
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Checking permissions…</span>
        </Spinner>
      </Container>
    );
  }

  if (nationId > 0 && permsError) {
    return (
      <Container fluid className="py-4">
        <Alert variant="danger" className="d-flex justify-content-between align-items-center gap-2 mb-0">
          <span>{permsError}</span>
          <Button variant="outline-danger" size="sm" onClick={() => refreshPermissions()}>Retry</Button>
        </Alert>
      </Container>
    );
  }

  if (nationId > 0 && !canManageAny) {
    return <Navigate to="/entities" replace />;
  }

  return (
    <Container fluid className="py-4" data-testid="presentation-layouts-page">
      <PageBreadcrumb items={breadcrumbItems} />

      <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-md-center gap-3 mb-3">
        <div className="ntcog-page-title-block mb-0">
          <h1 className="ntcog-page-heading mb-1">Presentation Layouts</h1>
          <div className="d-flex align-items-center gap-2 mt-1">
            <span className="text-muted small">Department:</span>
            <span className="badge rounded-pill bg-body-secondary text-body border" data-testid="department-name">
              {(departmentName || "Unknown").toUpperCase()}
            </span>
          </div>
        </div>
        {departmentId > 0 && availableOriginScopes.length > 0 ? (
          <Button
            variant="primary"
            onClick={() => void openEditor(null)}
            disabled={!meta || busy}
            data-testid="new-layout-button"
          >
            <FiPlus className="me-2" aria-hidden />
            New layout
          </Button>
        ) : null}
      </div>

      <p className="text-muted">
        Layouts add named views to department reports alongside the default table. Users pick a layout from the
        Layout dropdown on the report.
      </p>

      {locked && lockedReason ? (
        <Alert variant="warning" data-testid="layouts-locked-reason">{lockedReason}</Alert>
      ) : null}
      {loadError ? <Alert variant="danger">{loadError}</Alert> : null}
      {metaError ? (
        <Alert variant="danger" data-testid="layouts-meta-error">{MESSAGE_META_LOAD_FAILED}</Alert>
      ) : null}

      <div className="ntcog-list-search-section mb-3">
        <SearchBar
          placeholder="Search layouts…"
          filterFields={FILTER_FIELDS}
          initialQuery={searchQuery}
          activeFilterCount={activeFilterCount}
          loading={loading}
          onQueryChange={setSearchQuery}
          onFiltersApply={setActiveFilters}
          onClear={() => {
            setSearchQuery("");
            setActiveFilters({});
          }}
          testId="presentation-layout-search-bar"
        />
      </div>

      {loading ? (
        <div className="py-4">
          <Spinner animation="border" size="sm" className="me-2" aria-hidden />
          Loading…
        </div>
      ) : (
        <Table responsive hover className="align-middle mb-0" data-testid="presentation-layouts-table">
          <thead>
            <tr>
              <th>Order</th>
              <th>Name</th>
              <th>Report level</th>
              {/* <th>Sections</th> */}
              <th>Status</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {layouts.length === 0 && !loadError ? (
              <tr>
                <td colSpan={6} className="text-muted">No presentation layouts found.</td>
              </tr>
            ) : (
              layouts.map((layout, index) => {
                const id = layout.presentation_layout_id;
                const canReorderWith = (other?: PresentationLayoutListItem) =>
                  !isFiltered && !busy && other !== undefined && can(layout.origin_scope, "update") && can(other.origin_scope, "update");
                return (
                  <tr key={id}>
                    <td className="text-nowrap">
                      <span className="me-1" data-testid={`layout-order-${id}`}>{layout.list_order}</span>
                      <Button size="sm" variant="link" aria-label={`Move ${layout.name} up`} disabled={!canReorderWith(layouts[index - 1])} onClick={() => handleMove(index, index - 1)} data-testid={`move-layout-up-${id}`}>
                        <FiArrowUp aria-hidden />
                      </Button>
                      <Button size="sm" variant="link" aria-label={`Move ${layout.name} down`} disabled={!canReorderWith(layouts[index + 1])} onClick={() => handleMove(index, index + 1)} data-testid={`move-layout-down-${id}`}>
                        <FiArrowDown aria-hidden />
                      </Button>
                    </td>
                    <td>
                      <div className="fw-medium">{layout.name}</div>
                      {layout.description ? <div className="text-muted small">{layout.description}</div> : null}
                    </td>
                    <td>{ORIGIN_SCOPE_LABELS[layout.origin_scope] ?? layout.origin_scope}</td>
                    {/* <td>{layout.section_count}</td> */}
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <StatusPill active={layout.is_active} />
                        {layout.is_in_use ? <Badge bg="info" text="dark" title="Used on a report; it cannot be deleted or moved to another level.">In use</Badge> : null}
                      </div>
                    </td>
                    <td className="text-end">
                      <TableActions aria-label={`Actions for ${layout.name}`}>
                        <TableActionButton
                          icon={<FiEye aria-hidden />}
                          label="Preview"
                          primary
                          variant="outline-secondary"
                          onClick={() => setPreviewTarget(layout)}
                          data-testid={`preview-layout-button-${id}`}
                        />
                        {can(layout.origin_scope, "update") ? (
                          <TableActionButton
                            icon={<FiEdit2 aria-hidden />}
                            label="Edit"
                            variant="outline-secondary"
                            disabled={!meta || busy}
                            onClick={() => void openEditor(layout)}
                            data-testid={`edit-layout-button-${id}`}
                          />
                        ) : null}
                        {layout.is_active && !layout.is_in_use && !locked && can(layout.origin_scope, "updateOrigin") ? (
                          <TableActionButton
                            icon={<FiRefreshCw aria-hidden />}
                            label="Change report level"
                            variant="outline-secondary"
                            onClick={() => setOriginTarget(layout)}
                            data-testid={`change-layout-origin-button-${id}`}
                          />
                        ) : null}
                        {layout.is_active && can(layout.origin_scope, "disable") ? (
                          <TableActionButton
                            icon={<FiSlash aria-hidden />}
                            label="Disable"
                            variant="outline-secondary"
                            className="ntcog-table-action-btn--danger"
                            onClick={() => setStatusTarget({ kind: "disable", layout })}
                            data-testid={`disable-layout-button-${id}`}
                          />
                        ) : null}
                        {!layout.is_active && can(layout.origin_scope, "enable") ? (
                          <TableActionButton
                            icon={<FiCheckCircle aria-hidden />}
                            label="Enable"
                            variant="outline-secondary"
                            className="ntcog-table-action-btn--success"
                            onClick={() => setStatusTarget({ kind: "enable", layout })}
                            data-testid={`enable-layout-button-${id}`}
                          />
                        ) : null}
                        {!layout.is_in_use && can(layout.origin_scope, "delete") ? (
                          <TableActionButton
                            icon={<FiTrash2 aria-hidden />}
                            label="Delete"
                            variant="outline-secondary"
                            className="ntcog-table-action-btn--danger"
                            onClick={() => setDeleteTarget(layout)}
                            data-testid={`delete-layout-button-${id}`}
                          />
                        ) : null}
                      </TableActions>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      )}
      {isFiltered && layouts.length > 0 ? (
        <p className="text-muted small mt-2">Clear the search and filters to change the order.</p>
      ) : null}

      {meta ? (
        <PresentationLayoutEditorModal
          show={editor !== null}
          layout={editor?.layout ?? null}
          meta={meta}
          deductibles={deductibles}
          availableOriginScopes={availableOriginScopes}
          structureLocked={locked}
          lockedReason={lockedReason}
          busy={busy}
          onHide={() => setEditor(null)}
          onSave={(payload) => void handleSave(payload)}
        />
      ) : null}

      <PresentationLayoutPreviewModal
        show={previewTarget !== null}
        onHide={() => setPreviewTarget(null)}
        nationId={nationId}
        departmentId={departmentId}
        layout={previewTarget}
      />

      <ChangePresentationLayoutOriginModal
        show={originTarget !== null}
        layout={originTarget}
        availableOriginScopes={availableOriginScopes}
        busy={busy}
        onHide={() => setOriginTarget(null)}
        onSave={async (origin: OriginScope) => {
          if (!originTarget) return;
          const ok = await runWrite(
            () => changePresentationLayoutOrigin(nationId, departmentId, originTarget.presentation_layout_id, origin),
            "Report level updated."
          );
          if (ok) setOriginTarget(null);
        }}
      />

      <StatusChangeConfirmModal
        show={statusTarget !== null}
        kind={statusTarget?.kind ?? "disable"}
        entityLabel={STATUS_CHANGE_ENTITY_LABEL}
        entityName={statusTarget?.layout.name ?? ""}
        bodyText={statusTarget ? statusChangeBody(statusTarget.kind, statusTarget.layout.name) : undefined}
        busy={busy}
        onCancel={() => !busy && setStatusTarget(null)}
        onConfirm={async () => {
          if (!statusTarget) return;
          const id = statusTarget.layout.presentation_layout_id;
          const ok = statusTarget.kind === "disable"
            ? await runWrite(() => disablePresentationLayout(nationId, departmentId, id), "Disabled.")
            : await runWrite(() => enablePresentationLayout(nationId, departmentId, id), "Enabled.");
          if (ok) setStatusTarget(null);
        }}
      />

      <Modal show={deleteTarget !== null} onHide={() => !busy && setDeleteTarget(null)} centered backdrop="static" keyboard={!busy}>
        <Modal.Header closeButton={!busy}>
          <Modal.Title>Delete presentation layout</Modal.Title>
        </Modal.Header>
        <Modal.Body data-testid="delete-layout-confirm">
          Delete <strong>{deleteTarget?.name}</strong>? It has never been used on a report, so nothing else changes.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setDeleteTarget(null)} disabled={busy}>Cancel</Button>
          <Button
            variant="danger"
            disabled={busy}
            onClick={async () => {
              if (!deleteTarget) return;
              const ok = await runWrite(
                () => deletePresentationLayout(nationId, departmentId, deleteTarget.presentation_layout_id),
                "Deleted."
              );
              if (ok) setDeleteTarget(null);
            }}
          >
            {busy ? <Spinner animation="border" size="sm" aria-hidden /> : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ManagePresentationLayouts;
